import React from "react";
import { Box } from "@mui/material";
import { keyframes } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import { colors } from "../../theme/colors";

const particleFloat = keyframes({
    "0%": {
        transform: "translate3d(0px, 0px, 0px) scale(0.96)",
        opacity: 0,
    },

    "10%": {
        opacity: 0.08,
    },

    "35%": {
        transform: "translate3d(6px, -24px, 0px) scale(1)",
        opacity: 0.14,
    },

    "65%": {
        transform: "translate3d(-4px, -52px, 0px) scale(1.04)",
        opacity: 0.12,
    },

    "90%": {
        opacity: 0.06,
    },

    "100%": {
        transform: "translate3d(10px, -92px, 0px) scale(0.94)",
        opacity: 0,
    },
});

const ambientDrift = keyframes({
    "0%": {
        transform: "translate3d(-1%, 0%, 0) scale(1)",
        opacity: 0.16,
    },

    "50%": {
        transform: "translate3d(1%, -2%, 0) scale(1.02)",
        opacity: 0.19,
    },

    "100%": {
        transform: "translate3d(-1%, 0%, 0) scale(1)",
        opacity: 0.16,
    },
});

const ambientDriftSecondary = keyframes({
    "0%": {
        transform: "translate3d(0%, 0%, 0) scale(1)",
        opacity: 0.06,
    },

    "30%": {
        transform: "translate3d(-1.5%, 1%, 0) scale(1.02)",
        opacity: 0.08,
    },

    "60%": {
        transform: "translate3d(1%, -1%, 0) scale(1.03)",
        opacity: 0.09,
    },

    "100%": {
        transform: "translate3d(0%, 0%, 0) scale(1)",
        opacity: 0.06,
    },
});

const particleAnim = `${particleFloat} linear infinite`;
const driftAnim = `${ambientDrift} 18s cubic-bezier(0.4, 0, 0.2, 1) infinite`;
const driftSecondaryAnim = `${ambientDriftSecondary} 26s ease-in-out infinite alternate`;

const PARTICLES = [
    {
        left: "8%",
        top: "18%",
        delay: "0s",
        duration: "14s",
        size: 3,
        blur: 0,
        opacity: 0.12,
    },
    {
        right: "12%",
        top: "22%",
        delay: "2s",
        duration: "18s",
        size: 6,
        blur: 2,
        opacity: 0.08,
    },
    {
        left: "22%",
        top: "12%",
        delay: "4s",
        duration: "16s",
        size: 2,
        blur: 0,
        opacity: 0.12,
    },
    {
        left: "34%",
        top: "58%",
        delay: "1s",
        duration: "20s",
        size: 5,
        blur: 3,
        opacity: 0.07,
    },
    {
        left: "48%",
        top: "72%",
        delay: "5s",
        duration: "17s",
        size: 3,
        blur: 1,
        opacity: 0.11,
    },
    {
        left: "62%",
        top: "14%",
        delay: "6s",
        duration: "21s",
        size: 7,
        blur: 4,
        opacity: 0.05,
    },
    {
        left: "76%",
        top: "48%",
        delay: "3s",
        duration: "15s",
        size: 4,
        blur: 1,
        opacity: 0.1,
    },
    {
        left: "88%",
        top: "68%",
        delay: "7s",
        duration: "19s",
        size: 3,
        blur: 0,
        opacity: 0.09,
    },
    {
        left: "14%",
        top: "78%",
        delay: "8s",
        duration: "22s",
        size: 6,
        blur: 4,
        opacity: 0.05,
    },
    {
        left: "56%",
        top: "38%",
        delay: "10s",
        duration: "16s",
        size: 2,
        blur: 0,
        opacity: 0.12,
    },
];

export default function LobbyParticles() {
    return (
        <Box
            sx={{
                position: "absolute",
                inset: 0,
                zIndex: 1,
                pointerEvents: "none",
                overflow: "hidden",
            }}
        >
            {/* Ambient cinematic glow */}
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    background: `
                        radial-gradient(
                            circle at center,
                            ${alpha(colors.accent.main, 0.08)} 0%,
                            transparent 58%
                        )
                    `,
                    animation: driftAnim,
                    willChange: "opacity",
                }}
            />

            {/* Top ambient light */}
            <Box
                sx={{
                    position: "absolute",
                    top: "-15%",
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: "60rem",
                    height: "28rem",
                    borderRadius: "50%",
                    background: `
                        radial-gradient(
                            ellipse at center,
                            ${alpha(colors.accent.main, 0.12)} 0%,
                            transparent 72%
                        )
                    `,
                    filter: "blur(56px)",
                    opacity: 0.16,
                    animation: driftAnim,
                    willChange: "opacity",
                }}
            />

            {/* Floating particles */}
            {PARTICLES.map((p, index) => (
                <Box
                    key={index}
                    sx={{
                        position: "absolute",
                        width: `${p.size}px`,
                        height: `${p.size}px`,
                        borderRadius: "999px",
                        background: `
                            radial-gradient(
                                circle at center,
                                ${alpha(colors.accent.light || "#FFD580", 0.95)} 0%,
                                ${alpha(colors.accent.main, 0.55)} 45%,
                                transparent 100%
                            )
                        `,
                        opacity: 0,
                        filter: `blur(${p.blur}px)`,
                        animation: particleAnim,
                        animationDelay: p.delay,
                        animationDuration: p.duration,
                        willChange: "transform, opacity",
                        ...(p.left != null ? { left: p.left } : {}),
                        ...(p.top != null ? { top: p.top } : {}),
                        ...(p.right != null ? { right: p.right } : {}),
                        ...(p.bottom != null ? { bottom: p.bottom } : {}),
                    }}
                />
            ))}

            {/* Subtle cinematic noise overlay */}
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    opacity: 0.025,
                    backgroundImage: `
                        radial-gradient(rgba(255,255,255,0.35) 0.5px, transparent 0.5px)
                    `,
                    backgroundSize: "4px 4px",
                    mixBlendMode: "soft-light",
                }}
            />

            <Box
                sx={{
                    position: "absolute",
                    inset: "-20%",
                    background: `
                        radial-gradient(
                            circle at center,
                            rgba(255, 170, 60, 0.10) 0%,
                            rgba(255, 140, 0, 0.05) 22%,
                            transparent 60%
                        )
                    `,
                    filter: "blur(80px)",
                    opacity: 0.14,
                    animation: `${ambientDrift} 18s cubic-bezier(0.4, 0, 0.2, 1) infinite`,
                    pointerEvents: "none",
                    willChange: "transform, opacity",
                }}
            />
            <Box
                sx={{
                    position: "absolute",
                    top: "-10%",
                    right: "-5%",
                    width: "40rem",
                    height: "24rem",
                    borderRadius: "50%",
                    background: `
                        radial-gradient(
                            ellipse at center,
                            rgba(255, 200, 120, 0.05) 0%,
                            transparent 72%
                        )
                    `,
                    filter: "blur(70px)",
                    opacity: 0.08,
                    animation: driftSecondaryAnim,
                    pointerEvents: "none",
                }}
            />

            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    opacity: 0.022,
                    backgroundImage:
                        "radial-gradient(rgba(255,255,255,0.32) 0.5px, transparent 0.5px)",
                    backgroundSize: "4px 4px",
                    mixBlendMode: "soft-light",
                    pointerEvents: "none",
                }}
            />

            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    background: `
                        radial-gradient(
                            circle at center,
                            transparent 42%,
                            rgba(0,0,0,0.34) 100%
                        )
                    `,
                    pointerEvents: "none",
                }}
            />
        </Box>
    );
}